/** This module provides utilities for image processing, including resizing images. */

/**
 * Helper para processar URL da imagem
 * Retorna a URL direta (CORS configurado no bucket do Google Cloud Storage)
 * @param url - URL original da imagem
 * @returns URL da imagem
 */
export const getProxiedImageUrl = (url: string): string => {
  return url || ''
}

/**
 * Resizes an image file to fit within specified maximum dimensions.
 * @param file - The image file to resize
 * @param maxWidth - The maximum width of the resized image
 * @param maxHeight - The maximum height of the resized image
 * @param callback - A callback function that receives the resized image as a Blob
 * @returns {void} - This function does not return a value; it uses a callback to return the resized image.
 */
export const resizeImage = (
  file: File,
  maxWidth: number,
  maxHeight: number,
  callback: (resizedBlob: Blob | null) => void
): void => {
  const reader = new FileReader()

  reader.onload = event => {
    if (!event.target?.result) return

    const img = new Image()
    img.onload = () => {
      let { width, height } = img

      // keeping the aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const scale = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * scale)
        height = Math.round(height * scale)
      }

      // create a canvas element to resize the image
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        console.error('Erro ao obter o contexto do canvas.')
        return
      }

      ctx.drawImage(img, 0, 0, width, height)

      // Convert to blob and return via callback
      canvas.toBlob(blob => callback(blob), file.type)
    }
    img.src = event.target.result as string
  }

  reader.readAsDataURL(file)
}

/**
 * This function resizes an image file to fit within specified maximum dimensions and returns it as a base64 string.
 * @param file - The image file to resize
 * @param maxWidth - The maximum width of the resized image
 * @param maxHeight - The maximum height of the resized image
 * @param callback - A callback function that receives the resized image as a base64 string
 * @return {void} - This function does not return a value; it uses a callback to return the resized image as a base64 string.
 */
export const resizeImageToBase64 = (
  file: File,
  maxWidth: number,
  maxHeight: number,
  callback: (base64: string | null) => void
): void => {
  const reader = new FileReader()

  reader.onload = event => {
    if (!event.target?.result) return

    const img = new Image()
    img.onload = () => {
      let { width, height } = img

      // keeping the aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const scale = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * scale)
        height = Math.round(height * scale)
      }

      // create a canvas element to resize the image
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        console.error('Erro ao obter o contexto do canvas.')
        return
      }

      ctx.drawImage(img, 0, 0, width, height)

      // convert to base64 and return via callback
      const base64 = canvas.toDataURL(file.type)
      callback(base64)
    }
    img.src = event.target.result as string
  }

  reader.readAsDataURL(file)
}

/** Calcula as dimensões finais preservando a proporção. */
const scaleToMaxWidth = (
  width: number,
  height: number,
  maxWidth: number
): { width: number; height: number } => {
  if (width <= maxWidth) return { width, height }

  return {
    width: maxWidth,
    height: Math.round((height * maxWidth) / width)
  }
}

/** Decodifica o blob fora da main thread quando o browser suporta. */
const decodeBlob = async (
  blob: Blob,
  maxWidth: number
): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof createImageBitmap === 'function') {
    // As opções de resize são ignoradas em alguns browsers (Safari), então o
    // redimensionamento definitivo acontece no `drawImage` abaixo de qualquer
    // forma — aqui elas são só um atalho para decodificar menos pixels.
    try {
      return await createImageBitmap(blob, {
        resizeWidth: maxWidth,
        resizeQuality: 'high'
      })
    } catch {
      return await createImageBitmap(blob)
    }
  }

  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = new Image()
    img.src = objectUrl
    await img.decode()
    return img
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Comprime uma imagem remota reduzindo dimensão e qualidade.
 *
 * Devolve um object URL, e não um data URL: base64 é ~33% maior que o binário
 * e obriga o browser a decodificar a string de novo ao pintar o `<img>` — num
 * raio-X em resolução original isso pesa. **Quem chama é responsável por
 * revogar o URL** (`URL.revokeObjectURL`) quando a imagem sair de cena.
 *
 * O download é feito por `fetch` para que a decodificação saia da main thread
 * via `createImageBitmap`, em vez de bloquear a UI num `<img>` + `toDataURL`.
 *
 * @param imageUrl - URL da imagem original
 * @param maxWidth - Largura máxima (padrão: 800px)
 * @param quality - Qualidade JPEG 0-1 (padrão: 0.7)
 * @returns Promise com o object URL da imagem comprimida
 */
export const compressImageToObjectUrl = async (
  imageUrl: string,
  maxWidth: number = 800,
  quality: number = 0.7
): Promise<string> => {
  // O bucket precisa responder com CORS; sem isso o canvas fica "tainted" e a
  // exportação falharia mais adiante, na geração do PDF.
  const response = await fetch(imageUrl, { mode: 'cors' })
  if (!response.ok) {
    throw new Error(`Failed to load image: ${imageUrl} (${response.status})`)
  }

  const source = await decodeBlob(await response.blob(), maxWidth)
  const { width, height } = scaleToMaxWidth(
    source.width,
    source.height,
    maxWidth
  )

  const canvas =
    typeof OffscreenCanvas === 'function'
      ? new OffscreenCanvas(width, height)
      : Object.assign(document.createElement('canvas'), { width, height })

  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null

  if (!ctx) {
    throw new Error('Could not get canvas context')
  }

  ctx.drawImage(source, 0, 0, width, height)
  if ('close' in source) source.close()

  // Testado por capability, e não com `instanceof OffscreenCanvas`: no browser
  // que caiu no fallback o global não existe, e a própria comparação lançaria.
  const compressed =
    'convertToBlob' in canvas
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality })
      : await new Promise<Blob | null>(resolve =>
          canvas.toBlob(resolve, 'image/jpeg', quality)
        )

  if (!compressed) {
    throw new Error(`Failed to compress image: ${imageUrl}`)
  }

  return URL.createObjectURL(compressed)
}
